import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Ticket } from "lucide-react";

/**
 * AdminAirlineTourCodes
 * Net-fare contract code applied at PNR creation time via Travelport's
 * AirCreateReservationReq. The XML carries it in both
 * `<com:AccountingRemark Category="TOUR">` and the AirPricingModifiers
 * AccountCodes block so the airline host AND BSP cycle both pick it up.
 * Configured per IATA carrier; applied instantly.
 */
export default function AdminAirlineTourCodes() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/airline-tour-codes"); setRows(data.items || []); }
    catch (_e) { toast.error("Failed to load tour codes"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit) return;
    if (!edit.airline_code || edit.airline_code.length < 2) { toast.error("IATA code required"); return; }
    if (!edit.tour_code || !edit.tour_code.trim()) { toast.error("Tour code required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/airline-tour-codes", {
        airline_code: edit.airline_code.toUpperCase(),
        tour_code: edit.tour_code.toUpperCase().trim(),
        active: edit.active !== false,
        note: edit.note || null,
      });
      toast.success(`Tour code saved for ${edit.airline_code.toUpperCase()}`);
      setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (code) => {
    if (!window.confirm(`Delete tour code for ${code}?`)) return;
    try { await api.delete(`/admin/airline-tour-codes/${code}`); toast.success("Deleted"); load(); }
    catch (_e) { toast.error("Delete failed"); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="admin-airline-tour-codes">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[var(--brand-accent)] flex items-center gap-2">
            <Ticket className="w-5 h-5 text-[var(--brand-primary)]" /> Airline Tour Codes
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl leading-relaxed">
            Net-fare <strong>tour code</strong> attached to <code>AirCreateReservationReq</code> at booking time via <code>&lt;com:AccountingRemark Category="TOUR"&gt;</code> + <code>AirPricingModifiers/AccountCodes</code>. Applied instantly on the PNR — the airline host and BSP cycle both honour it.
          </p>
        </div>
        <Button data-testid="airline-tour-add-btn" onClick={() => setEdit({ airline_code: "", tour_code: "", active: true, note: "" })} className="rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">
          <Plus className="w-4 h-4 mr-1" /> Add tour code
        </Button>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Airline (IATA)</TableHead>
            <TableHead>Tour Code</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Note</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.airline_code} data-testid={`tour-row-${r.airline_code}`}>
                <TableCell className="font-mono font-bold text-[var(--brand-accent)]">{r.airline_code}</TableCell>
                <TableCell className="font-mono font-semibold text-[var(--brand-primary-dark)]">{r.tour_code}</TableCell>
                <TableCell>{r.active ? <Badge className="bg-emerald-50 text-emerald-700 border-0">Active</Badge> : <Badge variant="outline">Disabled</Badge>}</TableCell>
                <TableCell className="text-xs text-slate-500 max-w-xs truncate">{r.note || "—"}</TableCell>
                <TableCell className="text-xs text-slate-400">{r.updated_at?.slice(0, 16).replace("T", " ")}</TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`tour-edit-${r.airline_code}`} size="sm" variant="ghost" onClick={() => setEdit({ ...r })}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button data-testid={`tour-del-${r.airline_code}`} size="sm" variant="ghost" onClick={() => del(r.airline_code)} className="text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No tour codes configured yet. Click <strong className="text-[var(--brand-accent)]">Add tour code</strong> to register one.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }}>
        <DialogContent data-testid="airline-tour-dialog">
          <DialogHeader>
            <DialogTitle>{edit?.id ? `Edit ${edit.airline_code}` : "Add airline tour code"}</DialogTitle>
            <DialogDescription>Net-fare code applied at PNR creation via AccountingRemark + AccountCodes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Airline IATA code</Label>
              <Input data-testid="tour-airline-code" value={edit?.airline_code || ""} onChange={(e) => setEdit({ ...edit, airline_code: e.target.value.toUpperCase().slice(0, 3) })} placeholder="EK / MS / SV" className="font-mono uppercase" />
            </div>
            <div>
              <Label>Tour code</Label>
              <Input data-testid="tour-code" value={edit?.tour_code || ""} onChange={(e) => setEdit({ ...edit, tour_code: e.target.value.toUpperCase() })} placeholder="EKBSP2026Q1 / TPEGYPT01" className="font-mono uppercase" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input data-testid="tour-note" value={edit?.note || ""} onChange={(e) => setEdit({ ...edit, note: e.target.value })} placeholder="e.g. Emirates Q1 contract for ME originating fares" />
            </div>
            <div className="flex items-center gap-2">
              <Switch data-testid="tour-active" checked={edit?.active !== false} onCheckedChange={(v) => setEdit({ ...edit, active: v })} />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
            <Button data-testid="tour-save-btn" onClick={save} disabled={busy} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
